// src/mobile/MobileNavbar.js
// Minimal top navigation bar for mobile

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ArrowRight } from 'lucide-react';
import './styles/mobile-navbar.css';

const MobileNavbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const navItems = [
    { label: 'Home', href: '/', anchor: 'hero' },
    { label: 'Capabilities', href: '/', anchor: 'capabilities' },
    { label: 'Approach', href: '/', anchor: 'approach' },
    { label: 'Demos', href: '/', anchor: 'demos' },
    { label: 'Resources', href: '/resources', isRoute: true },
    // { label: 'Team', href: '/', anchor: 'team' }, // Temporarily hidden
    { label: 'Contact', href: '/', anchor: 'contact' },
  ];

  const handleNavClick = (e, item) => {
    e.preventDefault();
    setIsMenuOpen(false);

    if (item.isRoute) {
      // Navigate to route
      navigate(item.href);
    } else if (item.anchor) {
      // Check if we're on the home page
      if (location.pathname === '/') {
        // Already on home, scroll to section
        const element = document.getElementById(item.anchor);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      } else {
        // Navigate to home page with anchor hash
        navigate(`/#${item.anchor}`);
        // Increased timeout to allow page to fully render before scrolling
        setTimeout(() => {
          const element = document.getElementById(item.anchor);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 400);
      }
    }
  };

  return (
    <>
      {/* Top Header Bar */}
      <header className="mobile-navbar">
        <div className="mobile-navbar__inner">
          {/* Logo */}
          <a href="/" className="mobile-navbar__logo" onClick={(e) => handleNavClick(e, { href: '/', anchor: 'hero' })}>
            <span className="mobile-navbar__logo-icon">R</span>
            <span className="mobile-navbar__logo-text">Rosalytics</span>
          </a>

          {/* Hamburger Menu Button */}
          <motion.button
            className="mobile-navbar__menu-btn"
            onClick={toggleMenu}
            whileTap={{ scale: 0.95 }}
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </motion.button>
        </div>
      </header>

      {/* Full Screen Menu Overlay */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            className="mobile-menu-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <motion.nav
              className="mobile-menu"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            >
              <ul className="mobile-menu__list">
                {navItems.map((item, index) => (
                  <motion.li
                    key={item.label}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 + index * 0.05 }}
                  >
                    <a
                      href={item.href}
                      className="mobile-menu__link"
                      onClick={(e) => handleNavClick(e, item)}
                    >
                      {item.label}
                      <ArrowRight size={18} className="mobile-menu__arrow" />
                    </a>
                  </motion.li>
                ))}
              </ul>

              {/* CTA in Menu */}
              <motion.div
                className="mobile-menu__cta"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <a
                  href="/#contact"
                  className="mobile-menu__cta-btn"
                  onClick={(e) => handleNavClick(e, { href: '/', anchor: 'contact' })}
                >
                  Start a Conversation
                  <ArrowRight size={18} />
                </a>
              </motion.div>
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default MobileNavbar;
